"use client";

import { globalStyleObj } from "@/app/assets/styles";
import {
  LanguageTabs,
  PageRefresh,
  SubmitButton,
  TextEditor,
  TranslationForm,
} from "@/components";
import { useErrorToast, useGenerateSlug, useSuccessToast } from "@/lib/hooks";
import { PhotoGalleryValidationSchema } from "@/schemas/pagesSchema/media/PhotoGalleryValidationSchema";
import { useAppSelector } from "@/store/hooks";
import { getCustomTheme } from "@/utils/colors";
import {
  getDateInputField,
  getImageInputField,
  getMediaValidationSchema,
  getTextAreaInputField,
  getTextEditorField,
  getTextInputField,
} from "@/utils/helper-functions";
import { usePageRefresh } from "@/utils/refreshPage";
import { getTranslationDetails } from "@/utils/translation-helper";
import { zodResolver } from "@hookform/resolvers/zod";
import { isEqual } from "lodash";
import { useTheme } from "next-themes";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import CommonDatePicker from "../CommonDatePicker";

const CommonReactHookForm = ({
  userId,
  autoGenerateSlugRefKey,
  isMultiLanguageFileds = false,
  itemDetails = {},
  translationDetails = {},
  languages = [],
  filesList = [],
  allFiles = [],
  paginationDetails = {},
  validationKey = "",
  defaultState = {},
  translationFormFields = [],
  formFields = [],
  createFnc = () => {},
  updateFnc = () => {},
  searchValue,
  selectedFileType,
  adminRole,
  permissionsList,
}) => {
  const [activeTab, setActiveTab] = useState("en");
  const { isPending, pageRefresh } = usePageRefresh();
  const { layoutThemePrimaryColorType } = useAppSelector(
    (state) => state.layout
  );
  const { theme } = useTheme();
  const { bgColor, hoverBgColor, textColor, hexCode } = useMemo(
    () => getCustomTheme({ layoutThemePrimaryColorType }),
    [layoutThemePrimaryColorType]
  );
  const isItemDetailsExist = Object.keys(itemDetails).length > 0;
  const activeLanguages = isMultiLanguageFileds
    ? languages?.filter((lang) => lang?.status) || []
    : [];

  // Get validation schema
  const validationSchema = validationKey
    ? getMediaValidationSchema(validationKey)
    : null;

  // Handle React Hook Form
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    reset,
    setError,
    setValue,
  } = useForm({
    resolver: zodResolver(validationSchema),
    defaultValues: defaultState,
  });

  // console.log("Form Data: ", watch());

  // Relevant fields for comparison with the defaultState object (For Update Page)
  const relevantFields = defaultState;
  const hasChanges = !isEqual(relevantFields, watch());

  // Auto generate slug (If autoGenerateSlugRefKey is provided)
  const watchAutoGenerateSlugRefValue = autoGenerateSlugRefKey
    ? watch(autoGenerateSlugRefKey)
    : "";
  useEffect(() => {
    const generateSlug = () => {
      if (
        watchAutoGenerateSlugRefValue &&
        watchAutoGenerateSlugRefValue.length > 0
      ) {
        setError("slug", "");
        const formatedSlug = useGenerateSlug(watchAutoGenerateSlugRefValue);
        setValue("slug", formatedSlug);
      } else {
        setValue("slug", "");
      }
    };

    if (!isItemDetailsExist && autoGenerateSlugRefKey) {
      generateSlug();
    }
  }, [watchAutoGenerateSlugRefValue, setValue, isItemDetailsExist]);

  // Handle Image Change
  const onChangeImageFiled = useCallback(
    (keyname, id) => {
      if (keyname) {
        setValue(keyname, id);
      }
    },
    [setValue]
  );

  // Handle Validation Errors
  const handleValidationErrors = useCallback(
    (errors) => {
      Object.keys(errors).forEach((field) => {
        setError(field, {
          type: "server",
          message: errors[field].message,
        });
      });
    },
    [setError]
  );

  // Handle Form Submit Functionality
  const onSubmit = async (data) => {
    // Create API Call
    if (!isItemDetailsExist) {
      const response = await createFnc(userId, data);

      if (response.success) {
        useSuccessToast(response?.message || "Content created successfully.");
        reset();
      } else {
        if (response.errors) {
          handleValidationErrors(response.errors);
        } else {
          useErrorToast(response?.message || "Something went wrong.");
        }
      }
    }

    // Update API Call
    else {
      const updateResponse = await updateFnc(userId, itemDetails._id, data);

      if (updateResponse.success) {
        useSuccessToast(
          updateResponse?.message || "Content updated successfully."
        );

        pageRefresh();
      } else {
        if (updateResponse.errors) {
          handleValidationErrors(updateResponse.errors);
        } else {
          useErrorToast(updateResponse?.message || "Something went wrong.");
        }
      }
    }
  };

  // Handle Translation Form Submit
  const handleTranslationSubmit = async (translateData) => {
    const data = {
      ...watch(),
      translateData: { ...translateData, lang: activeTab },
    };

    await onSubmit(data);
  };

  // Handle Refresh Page Loading when update API runs
  if (isPending) {
    return <PageRefresh />;
  }

  return (
    <div
      className={`${globalStyleObj.backgroundLight900Dark300} mt-[40px] rounded-sm shadow-light overflow-hidden`}
    >
      {/* Multi Language Tabs */}
      {isMultiLanguageFileds &&
        isItemDetailsExist &&
        activeLanguages.length > 1 && (
          <LanguageTabs
            languages={activeLanguages}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            colorGrade={{ bgColor, hoverBgColor, textColor, hexCode }}
          />
        )}

      {activeTab === "en" ? (
        formFields.length > 0 && (
          <form onSubmit={handleSubmit(onSubmit)} className={`p-3 sm:p-5`}>
            {formFields.map((field, index) => (
              <React.Fragment key={`form-filed-${index}`}>
                {/* Image Form Field */}
                {field.type === "IMAGE" &&
                  getImageInputField({
                    errors: errors,
                    isItemDetailsExist: isItemDetailsExist,
                    field: field,
                    userId: userId,
                    filesList: filesList,
                    allFiles: allFiles,
                    paginationDetails: paginationDetails,
                    searchValue: searchValue,
                    selectedFileType: selectedFileType,
                    adminRole: adminRole,
                    permissionsList: permissionsList,
                    onChangeImageFiled: onChangeImageFiled,
                    itemDetails: itemDetails,
                  })}

                {/* Text Input Field */}
                {field.type === "TEXT" &&
                  getTextInputField({
                    field: field,
                    control: control,
                    errors: errors,
                    isItemDetailsExist: isItemDetailsExist,
                  })}

                {/* Text AREA Input Field */}
                {field.type === "TEXT_AREA" &&
                  getTextAreaInputField({
                    field: field,
                    control: control,
                    errors: errors,
                    isItemDetailsExist: isItemDetailsExist,
                  })}

                {/* Date Input Field */}
                {field.type === "DATE" && (
                  <CommonDatePicker
                    fieldName={field.fieldName}
                    fieldId={`${isItemDetailsExist ? `update-${field.fieldId}` : `${field.fieldId}`}`}
                    value={watch(field.fieldName)}
                    errors={errors}
                    errorsType={errors?.[field.fieldName]}
                    handleDateTimeChange={(date) =>
                      setValue(field.fieldName, date)
                    }
                    labelName={field?.lableName || ""}
                    labelStatus={field?.labelStatus || false}
                    placeholderText={field?.placeholderText || ""}
                    extraClass={field?.extraClasses || ""}
                    inputBoxMaxWidth={
                      field?.inputBoxMaxWidth || "max-w-[800px]"
                    }
                  />
                )}

                {/* Text Editor Input Field */}
                {field.type === "TEXT_EDITOR" && (
                  <TextEditor
                    field={field}
                    isItemDetailsExist={isItemDetailsExist}
                    watchDescription={watch(field.fieldName)}
                    setValue={setValue}
                  />
                )}
              </React.Fragment>
            ))}

            {/* Submit Button */}
            <SubmitButton
              isSubmitting={isSubmitting}
              hasChanges={hasChanges}
              isDetailsExist={isItemDetailsExist}
              colorGrade={{
                bgColor: bgColor,
                hoverBgColor: hoverBgColor,
                textColor: textColor,
                hexCode: hexCode,
              }}
              label={isItemDetailsExist ? "Update" : "Create"}
              // icon={<BiNews />}
            />
          </form>
        )
      ) : isMultiLanguageFileds ? (
        <TranslationForm
          fields={translationFormFields.map((field) => ({
            ...field,
            value:
              getTranslationDetails(translationDetails, activeTab)?.[
                field.name
              ] || "",
          }))}
          isSubmitting={isSubmitting}
          colorGrade={{ bgColor, hoverBgColor, textColor, hexCode }}
          handleTranslationSubmit={handleTranslationSubmit}
          theme={theme}
        />
      ) : null}
    </div>
  );
};

export default CommonReactHookForm;
